/**
 * Preparação de arquivos de mídia no momento da seleção, antes do upload.
 *
 * Resolve quatro problemas de uma vez:
 * 1. **Arquivo instável**: o `File` do input/drag é só uma referência ao disco —
 *    se o arquivo de origem some (ex: temporários do WhatsApp Desktop), preview
 *    e upload falham com "Network Error". Aqui o conteúdo é copiado para a
 *    memória na hora ("estabilizado"), tornando o restante do fluxo imune.
 * 2. **Formatos que quebram preview/galeria/PDF**: HEIC (iPhone) é convertido
 *    para JPEG via heic2any (WASM); WebP/GIF/BMP são normalizados para JPEG
 *    via canvas. O PDF do checklist (pdfkit) só embute JPEG/PNG.
 * 3. **PDF anexado como se fosse foto** (at-58): digitalizações costumam chegar
 *    em PDF. Cada página vira um JPEG aqui no navegador — o arquivo PDF em si
 *    não é guardado, então galeria, PDF do checklist, tela pública e auditoria
 *    seguem lidando só com imagem, sem nenhuma mudança de backend.
 * 4. **Limites de tamanho**: imagens até 30MB (teto de request do Cloud Run é
 *    ~32MB); vídeos até 500MB (vão por upload direto ao GCS); PDF até 200MB
 *    (não vai para o servidor — só as páginas rasterizadas vão).
 */

export const MAX_IMAGE_BYTES = 30 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 500 * 1024 * 1024;

/**
 * Teto de bytes do PDF anexado.
 *
 * **Não é o mesmo teto das imagens** (era, até 10/08/2026, e rejeitava
 * digitalização legítima com "PDF acima do limite de 30MB"): o arquivo PDF
 * nunca sobe para o backend — o que sobe são as páginas convertidas em JPEG,
 * e cada uma dessas é validada contra `MAX_IMAGE_BYTES` na saída. Os 30MB
 * existem por causa do teto de request do Cloud Run (~32MB), que não se
 * aplica a um arquivo que fica no navegador. O limite aqui é só memória do
 * navegador para rasterizar.
 */
export const MAX_PDF_BYTES = 200 * 1024 * 1024;

/**
 * Teto de páginas por PDF. Cada página vira um JPEG em memória e um upload
 * próprio, então o custo é linear — o teto existe para o navegador não morrer
 * calado num arquivo absurdo, não para limitar o uso real (digitalização de
 * dezenas de páginas é comum; era 50 até 10/08/2026 e barrava PDF de 52).
 */
export const MAX_PDF_PAGES = 500;

/** Largura-alvo da página rasterizada: legível para leitura de avaria, sem gerar arquivo gigante. */
const PDF_TARGET_WIDTH_PX = 2000;

/** Teto do fator de ampliação — página pequena não vira imagem absurda. */
const PDF_MAX_SCALE = 3;

/** Acima disso o arquivo não é copiado para a memória (vídeos grandes). */
const STABILIZE_MAX_BYTES = 100 * 1024 * 1024;

const JPEG_QUALITY = 0.92;

const IMAGE_MIME_BY_EXT: Record<string, string> = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.gif': 'image/gif',
  '.bmp': 'image/bmp',
  '.heic': 'image/heic',
  '.heif': 'image/heif',
};

const VIDEO_MIME_BY_EXT: Record<string, string> = {
  '.mp4': 'video/mp4',
  '.m4v': 'video/mp4',
  '.mov': 'video/quicktime',
  '.webm': 'video/webm',
  '.avi': 'video/x-msvideo',
  '.mkv': 'video/x-matroska',
  '.3gp': 'video/3gpp',
};

export interface RejectedMediaFile {
  file: File;
  reason: string;
}

export interface PreparedMediaFiles {
  ready: File[];
  rejected: RejectedMediaFile[];
}

const extOf = (name: string): string => {
  const dot = (name || '').lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot).toLowerCase();
};

const replaceExt = (name: string, newExt: string): string => {
  const dot = (name || '').lastIndexOf('.');
  return dot === -1 ? `${name}${newExt}` : `${name.slice(0, dot)}${newExt}`;
};

const mbLabel = (bytes: number) => `${Math.floor(bytes / (1024 * 1024))}MB`;

const isHeic = (file: File): boolean => {
  const type = (file.type || '').toLowerCase();
  if (type === 'image/heic' || type === 'image/heif') return true;
  const ext = extOf(file.name);
  return ext === '.heic' || ext === '.heif';
};

/** Mime efetivo do arquivo — inferido pela extensão quando o browser não informa. */
const effectiveMime = (file: File): string => {
  const type = (file.type || '').toLowerCase();
  if (type) return type;
  const ext = extOf(file.name);
  return IMAGE_MIME_BY_EXT[ext] || VIDEO_MIME_BY_EXT[ext] || '';
};

export const isVideoFile = (file: File): boolean => effectiveMime(file).startsWith('video/');

const isImageMime = (file: File): boolean => effectiveMime(file).startsWith('image/');

/** PDF anexado no lugar de foto (at-58) — vira uma imagem por página. */
export const isPdfFile = (file: File): boolean =>
  (file.type || '').toLowerCase() === 'application/pdf' || extOf(file.name) === '.pdf';

/**
 * Copia o conteúdo para a memória — o novo File não depende mais do arquivo
 * de origem no disco. Lança se o arquivo já não puder ser lido.
 */
const stabilize = async (file: File, name?: string, type?: string): Promise<File> => {
  const buffer = await file.arrayBuffer();
  return new File([buffer], name ?? file.name, {
    type: type ?? effectiveMime(file) ?? file.type,
    lastModified: file.lastModified,
  });
};

/** HEIC/HEIF (iPhone) → JPEG, no navegador (WASM). */
const convertHeicToJpeg = async (file: File): Promise<File> => {
  const heic2any = (await import('heic2any')).default;
  const output = await heic2any({ blob: file, toType: 'image/jpeg', quality: JPEG_QUALITY });
  const blob = Array.isArray(output) ? output[0] : output;
  return new File([blob], replaceExt(file.name, '.jpg'), {
    type: 'image/jpeg',
    lastModified: file.lastModified,
  });
};

/** Rasteriza formatos que o navegador decodifica (WebP/GIF/BMP) para JPEG. */
const rasterizeToJpeg = async (file: File): Promise<File> => {
  const bitmap = await createImageBitmap(file);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D indisponível.');
    // Fundo branco para formatos com transparência não virarem fundo preto
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
    );
    if (!blob) throw new Error('Falha ao converter a imagem.');

    return new File([blob], replaceExt(file.name, '.jpg'), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } finally {
    bitmap.close();
  }
};

/** Erro de conversão de PDF que já traz mensagem pronta para o usuário. */
class PdfConversionError extends Error {}

/**
 * PDF → uma imagem JPEG por página, no próprio navegador (at-58).
 *
 * O pdfjs entra por import dinâmico (como o heic2any) para não pesar o bundle
 * de quem nunca anexa PDF. A versão é pinada no 3.x de propósito: a 4 é ESM
 * puro e atrita com o webpack do CRA.
 *
 * `onProgress` existe porque o teto de páginas é alto: um PDF de centenas de
 * páginas leva bem mais que "alguns segundos", e sem contador a tela parece
 * travada. Chamado antes de cada página, e o `await` do render dá à interface
 * a chance de repintar entre elas.
 */
const convertPdfToJpegs = async (
  file: File,
  onProgress?: (page: number, total: number) => void,
): Promise<File[]> => {
  const pdfjs = await import('pdfjs-dist');
  // O worker precisa vir do próprio bundle — sem CDN (não há rede liberada
  // para storage externo em toda máquina da oficina, ver at-33).
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.js',
    import.meta.url,
  ).toString();

  const buffer = await file.arrayBuffer();

  let doc: any;
  try {
    doc = await pdfjs.getDocument({ data: buffer }).promise;
  } catch (e: any) {
    if (e?.name === 'PasswordException') {
      throw new PdfConversionError('PDF protegido por senha — remova a senha e anexe novamente');
    }
    throw new PdfConversionError('PDF inválido ou corrompido — abra o arquivo para conferir e anexe novamente');
  }

  try {
    if (doc.numPages > MAX_PDF_PAGES) {
      throw new PdfConversionError(
        `PDF com ${doc.numPages} páginas (máximo ${MAX_PDF_PAGES}) — divida o arquivo e anexe as partes`,
      );
    }

    const baseName = replaceExt(file.name, '');
    const pages: File[] = [];

    for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber += 1) {
      onProgress?.(pageNumber, doc.numPages);
      const page = await doc.getPage(pageNumber);
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = Math.min(PDF_MAX_SCALE, Math.max(1, PDF_TARGET_WIDTH_PX / baseViewport.width));
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new PdfConversionError('não foi possível converter o PDF (canvas indisponível no navegador)');

      // PDF costuma ter fundo transparente; sem isto a página vira preta no JPEG
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      await page.render({ canvasContext: ctx, viewport }).promise;
      page.cleanup();

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
      );
      if (!blob) throw new PdfConversionError(`não foi possível converter a página ${pageNumber} do PDF`);

      if (blob.size > MAX_IMAGE_BYTES) {
        throw new PdfConversionError(
          `a página ${pageNumber} ficou acima de ${mbLabel(MAX_IMAGE_BYTES)} após a conversão`,
        );
      }

      pages.push(
        new File([blob], `${baseName}-p${pageNumber}.jpg`, {
          type: 'image/jpeg',
          lastModified: file.lastModified,
        }),
      );
    }

    return pages;
  } finally {
    await doc.destroy().catch(() => undefined);
  }
};

const REASON_UNREADABLE =
  'não pôde ser lido — o arquivo foi movido ou apagado após a seleção. Salve-o em uma pasta (ex: Downloads) e anexe novamente';

const prepareImage = async (file: File): Promise<File> => {
  const type = effectiveMime(file);

  if (type === 'image/jpeg' || type === 'image/png') {
    return stabilize(file, undefined, type);
  }

  if (isHeic(file)) {
    // Estabiliza antes de converter — a conversão lê o arquivo do disco
    const stable = await stabilize(file);
    return convertHeicToJpeg(stable);
  }

  // Demais formatos de imagem: tenta normalizar para JPEG via canvas
  const stable = await stabilize(file);
  return rasterizeToJpeg(stable);
};

/**
 * Prepara os arquivos selecionados: valida tipo/tamanho, copia para a memória
 * e converte formatos problemáticos. Devolve os prontos e os rejeitados (com motivo).
 *
 * `allowPdf` (at-58) só é ligado pelo checklist: um PDF entra e sai como N
 * imagens, uma por página. `onPdfProgress` alimenta o aviso de progresso da
 * tela durante a conversão.
 */
export const prepareMediaFiles = async (
  files: File[],
  options?: {
    allowVideo?: boolean;
    allowPdf?: boolean;
    onPdfProgress?: (page: number, total: number) => void;
  }
): Promise<PreparedMediaFiles> => {
  const allowVideo = options?.allowVideo ?? false;
  const allowPdf = options?.allowPdf ?? false;
  const ready: File[] = [];
  const rejected: RejectedMediaFile[] = [];

  for (const file of files) {
    try {
      if (isPdfFile(file)) {
        if (!allowPdf) {
          rejected.push({ file, reason: 'tipo de arquivo não permitido — envie fotos ou vídeos' });
          continue;
        }
        if (file.size > MAX_PDF_BYTES) {
          rejected.push({ file, reason: `PDF acima do limite de ${mbLabel(MAX_PDF_BYTES)}` });
          continue;
        }

        // Sem `stabilize` aqui de propósito: a conversão lê o `arrayBuffer` como
        // primeiro passo, então não há janela para o arquivo sumir do disco no
        // meio do caminho (o que justifica a cópia nos outros formatos, que só
        // leem o blob depois). Estabilizar duplicaria o consumo de memória
        // justamente no formato onde ele já é o fator limitante — e o arquivo
        // que já não pode ser lido cai no `catch` com a mesma mensagem.
        ready.push(...(await convertPdfToJpegs(file, options?.onPdfProgress)));
        continue;
      }

      if (isVideoFile(file)) {
        if (!allowVideo) {
          rejected.push({ file, reason: 'vídeos não são permitidos aqui — envie apenas imagens' });
          continue;
        }
        if (file.size > MAX_VIDEO_BYTES) {
          rejected.push({ file, reason: `vídeo acima do limite de ${mbLabel(MAX_VIDEO_BYTES)}` });
          continue;
        }
        // Vídeos pequenos são estabilizados; os grandes seguem por referência
        // (copiar centenas de MB para a memória travaria o navegador).
        ready.push(file.size <= STABILIZE_MAX_BYTES ? await stabilize(file) : file);
        continue;
      }

      if (!isImageMime(file)) {
        rejected.push({ file, reason: 'tipo de arquivo não permitido — envie fotos ou vídeos' });
        continue;
      }

      if (file.size > MAX_IMAGE_BYTES) {
        rejected.push({ file, reason: `imagem acima do limite de ${mbLabel(MAX_IMAGE_BYTES)}` });
        continue;
      }

      const prepared = await prepareImage(file);

      if (prepared.size > MAX_IMAGE_BYTES) {
        rejected.push({ file, reason: `imagem acima do limite de ${mbLabel(MAX_IMAGE_BYTES)} após conversão` });
        continue;
      }

      ready.push(prepared);
    } catch (e) {
      // A conversão de PDF já sabe explicar o próprio motivo (senha, páginas
      // demais, arquivo corrompido) — não vale trocar por mensagem genérica.
      if (e instanceof PdfConversionError) {
        rejected.push({ file, reason: e.message });
        continue;
      }

      const convertible = isHeic(file) || (isImageMime(file) && !['image/jpeg', 'image/png'].includes(effectiveMime(file)));
      rejected.push({
        file,
        reason: convertible
          ? `não pôde ser convertido (${extOf(file.name) || 'formato desconhecido'}) — converta para JPG e anexe novamente, ou o arquivo ${REASON_UNREADABLE}`
          : isPdfFile(file)
            ? `não pôde ser lido como PDF — ${REASON_UNREADABLE}`
            : REASON_UNREADABLE,
      });
    }
  }

  return { ready, rejected };
};

/** Mensagem única para um lote de rejeições (para toast). */
export const describeRejectedFiles = (rejected: RejectedMediaFile[]): string =>
  rejected.map((r) => `"${r.file.name}": ${r.reason}`).join(' | ');
